import { useEffect, useState } from 'react';
import { getSocket, type ClientSocket } from '../lib/socket';
import type { GameRoom, GameRoomSummary, MatchConditions, StadiumAsset } from '../../../shared/types';

/**
 * Hook que expone el socket compartido y su estado de conexión.
 * Las suscripciones se limpian correctamente al desmontar.
 */
export function useSocket() {
  const [socket, setSocket] = useState<ClientSocket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | null = null;

    void getSocket().then((s) => {
      if (cancelled) return;
      const onConnect = () => setConnected(true);
      const onDisconnect = () => setConnected(false);
      s.on('connect', onConnect);
      s.on('disconnect', onDisconnect);
      detach = () => {
        s.off('connect', onConnect);
        s.off('disconnect', onDisconnect);
      };
      setSocket(s);
      setConnected(s.connected);
    });

    return () => {
      cancelled = true;
      detach?.();
    };
  }, []);

  return { socket, connected };
}

/** Hook de catálogo: lista de salas activas emitida por el servidor. */
export function useGameRooms(socket: ClientSocket | null) {
  const [rooms, setRooms] = useState<GameRoomSummary[]>([]);

  useEffect(() => {
    if (!socket) return;
    const onList = (incoming: GameRoomSummary[]) => setRooms(incoming);
    const refresh = () => socket.emit('rooms:refresh');
    socket.on('rooms:list', onList);
    // El socket es compartido: si ya estaba conectado, el `rooms:list`
    // inicial se emitió antes de montar el Hub, así que lo pedimos.
    socket.on('connect', refresh);
    if (socket.connected) refresh();
    return () => {
      socket.off('rooms:list', onList);
      socket.off('connect', refresh);
    };
  }, [socket]);

  return rooms;
}

/** Hook de estado de un lobby específico (room:state broadcast). */
export function useLobbyState(socket: ClientSocket | null, roomId: string | null) {
  const [room, setRoom] = useState<GameRoom | null>(null);

  useEffect(() => {
    if (!socket || !roomId) return;
    const onState = (incoming: GameRoom) => {
      if (incoming.id === roomId) setRoom(incoming);
    };
    socket.on('room:state', onState);
    return () => {
      socket.off('room:state', onState);
    };
  }, [socket, roomId]);

  return { room, setRoom };
}

/** Hook de cambios de condiciones visuales del lobby (estadio / clima / hora). */
export function useLobbyConditions(socket: ClientSocket | null, roomId: string | null) {
  const [conditions, setConditions] = useState<MatchConditions | null>(null);
  const [stadium, setStadium] = useState<StadiumAsset | null>(null);

  useEffect(() => {
    if (!socket || !roomId) return;

    const onChanged = (payload: { roomId: string; conditions: MatchConditions; stadium: StadiumAsset | null }) => {
      if (payload.roomId !== roomId) return;
      setConditions(payload.conditions);
      setStadium(payload.stadium);
    };

    socket.on('lobby:conditionsChanged', onChanged);
    return () => {
      socket.off('lobby:conditionsChanged', onChanged);
    };
  }, [socket, roomId]);

  return { conditions, stadium };
}
